import styled from 'styled-components';
import * as Popover from '@radix-ui/react-popover';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { isNotNull } from 'utils/tsHelpers';
import { noop } from 'utils/noop';
import { Calendar16 } from 'ui/icons/aurora/16px/Calendar16';
import { HORIZONTAL_ALIGNMENT, VERTICAL_ALIGNMENT } from 'ui/utility/types';
import { useDropdownPopoverAlignment } from 'ui/utility/hooks';
import { ALIGNMENT } from 'ui/selects';
import { DateRangePicker, DATE_PICKER_PORTAL_ID } from 'ui/datePicker';
import { DateAndCalendarWrapper } from 'ui/datePicker/DatePicker.styles';
import { colors } from 'ui/theme/colors';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { guessCurrentTimeZone } from 'utils/datetime';
import { getDatePickerDate } from '../ConditionDatePicker/utils';
import { ConditionDateRangePickerTriggerButton } from './ConditionDateRangePickerTrigger';

import { ConditionDateRangeProps, ConditionDateRange } from './types';
import { getDefaultDateRange, getOnDateRangeChange, isNotNullDateRange } from './utils';
import { useGetDisplayDate } from '../ConditionDatePicker/hooks/useGetDisplayDate';

export const DATA_TEST = {
  content: 'condition-date-range-picker-content',
};

const DateRangePickerWrapper = styled.div`
  background-color: ${colors.white};
  .react-calendar__tile--rangeEnd:not(.react-calendar__tile--rangeBothEnds)::after {
    z-index: 0;
  }
  .react-calendar__tile--rangeStart:not(.react-calendar__tile--rangeBothEnds)::after {
    z-index: 0;
  }
  [data-test='calendar-tile-date'] {
    z-index: 1;
  }
  .react-calendar__tile::before {
    z-index: 1;
  }
`;

export const ConditionDateRangePicker = ({
  value,
  defaultTimeZone,
  hasError = false,
  isDisabled = false,
  isReadOnly = false,
  isOpenByDefault = false,
  onChange = noop,
  placeholder = '',
  ...props
}: ConditionDateRangeProps) => {
  const [debouncedDateRange, dateRange, setDateRange] =
    useDebouncedState<ConditionDateRange | null>(value, 300);
  const convertedDateRangeValue = useMemo(() => getDefaultDateRange(dateRange), [dateRange]);
  const [timeZone, setTimeZone] = useState(defaultTimeZone ?? guessCurrentTimeZone());
  const [isEditing, setIsEditing] = useState(isOpenByDefault);

  const { popoverVerticalAlignment, popoverHorizontalAlignment } = useDropdownPopoverAlignment(
    ALIGNMENT.LEFT as HORIZONTAL_ALIGNMENT,
    ALIGNMENT.DOWN as VERTICAL_ALIGNMENT,
  );

  const onBlurSave = (editingMode: boolean) => {
    if (
      !editingMode &&
      isNotNullDateRange(convertedDateRangeValue) &&
      // Ensure we are not setting an exising null value to a null value as this causes undo/redo issues
      (isNotNull(dateRange) || isNotNull(value))
    ) {
      onChange(getOnDateRangeChange(convertedDateRangeValue));
    }
    setIsEditing(editingMode);
  };

  const triggerStartDate = useGetDisplayDate(
    getDefaultDateRange(debouncedDateRange).startDate,
    timeZone,
  );
  const triggerEndDate = useGetDisplayDate(
    getDefaultDateRange(debouncedDateRange).endDate,
    timeZone,
  );

  return (
    <Popover.Root open={isEditing} onOpenChange={onBlurSave}>
      <DateAndCalendarWrapper>
        <Popover.Trigger asChild>
          <ConditionDateRangePickerTriggerButton
            {...props}
            hasError={hasError}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            value={{
              startDate: isNotNull(debouncedDateRange) ? triggerStartDate : null,
              endDate: isNotNull(debouncedDateRange) ? triggerEndDate : null,
            }}
            onChange={onChange}
            placeholder={placeholder}
            DefaultIcon={<Calendar16 isColorInherited />}
          />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            data-portal-id={DATE_PICKER_PORTAL_ID}
            data-test={DATA_TEST.content}
            hideWhenDetached
            avoidCollisions={false}
            side={popoverVerticalAlignment}
            align={popoverHorizontalAlignment}
            sideOffset={4}
          >
            <DateRangePickerWrapper>
              <DateRangePicker
                date-picker-id={DATE_PICKER_PORTAL_ID}
                defaultTimeZone={timeZone}
                endDate={
                  convertedDateRangeValue?.endDate
                    ? moment(getDatePickerDate(convertedDateRangeValue.endDate))
                    : null
                }
                hideTimeZonePicker
                onDateRangeChange={(newRange) => {
                  const { startDate, endDate } = newRange;
                  const newStartDate = startDate?.toISOString() || null;
                  const newEndDate = endDate?.toISOString() || null;
                  setDateRange({ startDate: newStartDate, endDate: newEndDate });
                }}
                onTimeZoneChange={setTimeZone}
                showRelativeDatePresets
                startDate={
                  convertedDateRangeValue?.startDate
                    ? moment(getDatePickerDate(convertedDateRangeValue.startDate))
                    : null
                }
              />
            </DateRangePickerWrapper>
          </Popover.Content>
        </Popover.Portal>
      </DateAndCalendarWrapper>
    </Popover.Root>
  );
};
